import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Package, BookOpen, Users, Phone, Menu, X } from "lucide-react";

const navItems = [
  { path: "/", label: "Home", icon: Home },
  { path: "/products", label: "Products", icon: Package },
  { path: "/about", label: "About", icon: Users },
  { path: "/blog", label: "Blog", icon: BookOpen },
  { path: "/contact", label: "Contact", icon: Phone },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  return (
    <>
      {/* Desktop floating dock */}
      <motion.nav
        data-testid="desktop-nav"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
        className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 hidden md:flex items-center gap-1 px-2 py-2 rounded-full glass-strong shadow-2xl"
      >
        <Link to="/" className="mr-2 pl-3 flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/logo.jpg" alt="Drukst Biotech" className="w-8 h-8 rounded-full object-cover" />
          <span className="text-sm font-bold tracking-tight font-['Plus_Jakarta_Sans'] text-white/90">DRUKST</span>
        </Link>
        {navItems.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path || (path !== "/" && location.pathname.startsWith(path));
          return (
            <Link
              key={path}
              to={path}
              data-testid={`nav-${label.toLowerCase()}`}
              className={`relative flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                active ? "text-[#022C22] bg-[#2DD4BF]" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </Link>
          );
        })}
      </motion.nav>

      {/* Mobile header */}
      <motion.header
        data-testid="mobile-header"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`fixed top-0 left-0 right-0 z-50 md:hidden transition-all duration-300 ${
          scrolled ? "glass-strong" : ""
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="mobile-logo">
            <img src="/logo.jpg" alt="Drukst Biotech" className="w-9 h-9 rounded-full object-cover" />
            <span className="text-base font-bold font-['Plus_Jakarta_Sans'] text-white">DRUKST</span>
          </Link>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.header>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-[#020617]/95 backdrop-blur-xl" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 bottom-0 w-72 bg-[#0F172A] border-l border-white/10 pt-20 px-6"
            >
              {navItems.map(({ path, label, icon: Icon }, i) => {
                const active = location.pathname === path;
                return (
                  <motion.div
                    key={path}
                    initial={{ x: 40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={path}
                      data-testid={`mobile-nav-${label.toLowerCase()}`}
                      className={`flex items-center gap-3 py-4 border-b border-white/5 text-lg font-medium ${
                        active ? "text-[#2DD4BF]" : "text-[#94A3B8]"
                      }`}
                    >
                      <Icon size={20} />
                      {label}
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
